import moment, { Moment } from "moment";
import { ApprovalState } from '@features/holidays/models/approval-requests.models';
import { CompanyHoliday } from '@features/holidays/services/CompanyHolidayService';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { Period } from '@features/whereabouts/models/movements.models';
import { CustomLeaveDays, CustomNonWorkingDays, NonWorkingDays } from '@features/holidays/services/HolidayService';


export interface HolidayDay {
  status: string;
  date: string;
  am: boolean;
  pm: boolean;
  userId: string;
  state?: ApprovalState;
}

export interface HolidayGrouping {
  fromDate: string;
  toDate: string;
  userId: string;
  holidayBookingList: HolidayDay[];
  numberOfDays: number;
}

interface CarryOverInfo {
  carryOverExpiryDate: string;
  carryOverLimit: number;
  daysCarriedOver: number;
  daysTakenInPreviousYear: number;
  previousYearsAllowance: number;
}

export interface UserHolidayInfoResponse {
  holidayAllowance: number;
  totalAllowance: number;
  holidaysTaken: number;
  carriedOverDays: number;
  additionalDays: number;
  holidayBookings: HolidayDay[];
  holidayGroupings: HolidayGrouping[];
  companyHolidays: CompanyHoliday[];
  nonWorkingDays: NonWorkingDays[];
  customNonWorkingDays: CustomNonWorkingDays[];
  customLeaveDays: CustomLeaveDays[];
  autoCarryOverInfo?: CarryOverInfo;
  firstName: string;
  lastName: string;
  teamName?: string;
  userId: string;
}

export function holidayToHighlightedDayConverter(holiday: HolidayDay) {
  return {
    date: moment(holiday.date),
    am: holiday.am,
    pm: holiday.pm,
    color: holiday.state === ApprovalState.pending ? 'yellow' : 'blue',
  }
}

export function companyHolidayToHighlightedDayConverter(holiday: CompanyHoliday) {
  return {
    date: moment(holiday.date),
    am: 'HOLIDAY',
    pm: 'HOLIDAY',
    color: 'lightGreen',
  }
}

export function nonWorkingDayToHighlightedDayConverter(day: NonWorkingDays | CustomNonWorkingDays) {
  return {
    date: moment(day.date),
    am: day.am,
    pm: day.pm,
    color: 'grey',
  }
}

export function customLeaveDayToHighlightedDayConverter(day: CustomLeaveDays) {
  return {
    date: moment(day.date),
    am: day.am,
    pm: day.pm,
    color: 'lilac',
  }
}

// A run of consecutive custom-leave-type days of the same leave type, shown in "Your Bookings"
// alongside holiday groupings. Built on the client from the customLeaveDays the API already returns.
export interface CustomLeaveGrouping {
  fromDate: string;
  toDate: string;
  whereaboutsId: number;
  numberOfDays: number;
  state?: ApprovalState;
}

function customLeavePrimaryTypeId(day: CustomLeaveDays): number {
  return day.am ? day.amWhereaboutsId : day.pmWhereaboutsId;
}

function customLeaveDayCount(day: CustomLeaveDays): number {
  return (day.am ? 0.5 : 0) + (day.pm ? 0.5 : 0);
}

// Derive a run's approval state from its days: a pending day makes the whole run pending,
// otherwise approved/declined, mirroring how the holiday booking item resolves its status.
function combineApprovalState(existing?: ApprovalState, next?: ApprovalState): ApprovalState | undefined {
  const priority = [ApprovalState.pending, ApprovalState.approved, ApprovalState.declined];
  return priority.find(state => existing === state || next === state) ?? existing ?? next;
}

// Two leave days belong to the same run only when they are calendar-consecutive (gap of one day, so
// a weekend or any other gap breaks the run) and the previous day's afternoon joins the next day's
// morning. Mirrors the backend holiday grouping (HolidayUtils.groupConsecutiveHolidays).
function isConsecutiveLeaveDay(previous: CustomLeaveDays, next: CustomLeaveDays): boolean {
  return moment(next.date).diff(moment(previous.date), 'days') === 1 && previous.pm && next.am;
}

export function groupCustomLeaveDays(days: CustomLeaveDays[]): CustomLeaveGrouping[] {
  const sorted = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const groupings: CustomLeaveGrouping[] = [];
  let previous: CustomLeaveDays | undefined;

  sorted.forEach((day) => {
    const typeId = customLeavePrimaryTypeId(day);
    const current = groupings[groupings.length - 1];
    // Extend the run only for the same leave type AND strictly consecutive days.
    if (current && previous && current.whereaboutsId === typeId && isConsecutiveLeaveDay(previous, day)) {
      current.toDate = day.date;
      current.numberOfDays += customLeaveDayCount(day);
      current.state = combineApprovalState(current.state, day.state);
    } else {
      groupings.push({ fromDate: day.date, toDate: day.date, whereaboutsId: typeId, numberOfDays: customLeaveDayCount(day), state: day.state });
    }
    previous = day;
  });

  return groupings;
}

export class HolidayBookingRequest {
  public date: string;
  public am: boolean;
  public pm: boolean;
  constructor(private bookingDate: Moment, public userId: string, period: Period) {
    this.date = bookingDate.format(DATE_FORMAT);
    this.am = period === Period.AllDay || period === Period.AM;
    this.pm = period === Period.AllDay || period === Period.PM;
  }
}

export interface HolidayGroupBookingRequest {
  holidayBookingList: HolidayBookingRequest[];
  originalDates?: string[];
  leaveType?: number;
}

export interface UsersHolidayInfo {
  userId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  holidayAllowance: number;
  daysUsed: number;
  daysApproved: number;
  daysPending: number;
  autoCarryOverInfo?: CarryOverInfo;
  holidayAllowanceWithRollOver?: number;
}

